import { Box, Image, Link, Text } from '@chakra-ui/react';
import { useState } from 'react';
import type { Organization } from '@/api/Organization';
import { useOrganization } from '@/auth/AuthContext';
import type { User } from 'src/api/User';
import { OrgUtil } from 'src/data/OrgUtil';
import { useI18n } from 'src/i18n/I18nContext';
import { CooperaidLogo } from 'src/components/Logo';
import PereginBrand from 'src/components/PereginBrand';

type HeaderBrandVariant = 'desktop' | 'mobile' | 'drawer';

interface HeaderBrandProps {
    user: User | null;
    variant: HeaderBrandVariant;
    onClick?: () => void;
}

const OrganizationIdentity = ({ user, variant, organization, name }: {
    user: User; variant: HeaderBrandVariant; organization: Organization | null; name: string;
}) => {
    const [failedUrl, setFailedUrl] = useState<string | null>(null);
    const logoUrl = organization?.logoUrl?.trim();
    if (logoUrl && failedUrl !== logoUrl) {
        return <Image
            src={logoUrl}
            alt={name}
            width={variant === 'drawer' ? '7.5rem' : { base: '4.5rem', xl: '6rem' }}
            height={variant === 'drawer' ? '1.75rem' : '1.5rem'}
            objectFit='contain'
            onError={() => setFailedUrl(logoUrl)}
        />;
    }
    if (OrgUtil.isCooperaid(user)) {
        return (
            <CooperaidLogo
                aria-label='Co-operaid'
                width={variant === 'drawer' ? '7.5rem' : { base: '4.5rem', xl: '6rem' }}
                height={variant === 'drawer' ? '1.75rem' : '1.5rem'}
            />
        );
    }

    return (
        <Text
            maxW={variant === 'drawer' ? '10rem' : { base: '4.5rem', xl: '7rem' }}
            overflow='hidden'
            textOverflow='ellipsis'
            color='fg.muted'
            fontSize={{ base: 'xs', xl: 'sm' }}
            fontWeight='semibold'
            whiteSpace='nowrap'
        >
            {name}
        </Text>
    );
};

const HeaderBrand = ({ user, variant, onClick }: HeaderBrandProps) => {
    const { t } = useI18n();
    const loadedOrganization = useOrganization();
    const organization = loadedOrganization?.id === user?.organizationId ? loadedOrganization : null;
    const name = organization?.name ?? OrgUtil.orgName(user);
    const brandLabel = user
        ? t('brand.organizationHome', { organization: name })
        : t('brand.home');

    const content = user
        ? <OrganizationIdentity key={user.organizationId} user={user} variant={variant} organization={organization} name={name} />
        : <PereginBrand compactOnMobile={variant === 'mobile'} />;

    if (!onClick) {
        return <Box aria-label={brandLabel}>{content}</Box>;
    }

    return (
        <Link
            as='button'
            aria-label={brandLabel}
            display='flex'
            alignItems='center'
            cursor='pointer'
            flexShrink={0}
            textDecoration='none'
            onClick={onClick}
        >
            {content}
        </Link>
    );
};

export default HeaderBrand;
